import { GlobalStore, GridStore, UserStore } from "@roc/feature-app-core";
import { action, computed, flow, makeObservable, observable } from "mobx";
import { AddressDataResponse, CompResponse, ComparableProperty, LoanInformation, PropertyComparableFeedbackRequest } from "../types/propertyTypes";
import { CompToolService, SearchPropertyFormStore, CompToolFeedbackStore, capitalizeOnlyFirstLetter } from "@roc/feature-comp-tool";
import { CompToolFiltersStore } from "./compToolFiltersStore";
import { DateFormat, formatDate, generateUUID, isNil, segmentAnalytics, SegmentTrackerEventCompTool } from "@roc/feature-utils";
import { EditSubjectPropertyFormStore } from "./editSubjectPropertyFormStore";
import { PropertyCompFormStore } from "./propertyCompFormStore";
import { AddressPart, getAddressPart, getRecommendedPropertyImage } from "../utils/utils";
import { CompToolPhotosStore } from "./compToolPhotosStore";

export class CompToolStoreV2 {
  globalStore: GlobalStore;
  userStore: UserStore;
  searchPropertyFormStore: SearchPropertyFormStore;
  searchManuallyAddedComparableFormStore: SearchPropertyFormStore;
  compToolFiltersStore: CompToolFiltersStore;
  compToolFeedbackStore: CompToolFeedbackStore;
  editSubjectPropertyFormStore: EditSubjectPropertyFormStore;
  compToolPhotosStore: CompToolPhotosStore;
  propertyCompFormStores: PropertyCompFormStore[];
  compToolService: CompToolService;
  compResponse: CompResponse;
  firstThreeProperties: ComparableProperty[];
  filterOptions: any;
  addressDataResponse: AddressDataResponse;
  manuallyAddedComparables: ComparableProperty[] = [];
  subjectProperty: ComparableProperty;
  selectedProperty: ComparableProperty;
  loading: boolean;
  showCardView: boolean;
  compToolGridStore: GridStore;
  selectedCompareProperties: ComparableProperty[] = [];
  savedPropertyComps: any[] = [];
  gridApi: any;
  openCarouselPhotos: boolean;
  showCompareScreen: boolean;
  showWarningMessage: boolean;
  currentPage: number;
  editProperty: any;

  propertiesMapExtended: boolean;
  rentalComps: boolean;
  isInternal: boolean;
  internalUsername: string;
  loanInformation: LoanInformation;
  property: any;
  addressPropertyData: any;
  compareComments: string;
  heatmapData: any;

  constructor(globalStore, userStore) {
    this.userStore = userStore;
    this.globalStore = globalStore;
    this.searchPropertyFormStore = new SearchPropertyFormStore(globalStore)
    this.searchManuallyAddedComparableFormStore = new SearchPropertyFormStore(globalStore)
    this.compToolFiltersStore = new CompToolFiltersStore(globalStore)
    this.compToolService = new CompToolService();
    this.compToolFeedbackStore = new CompToolFeedbackStore(globalStore);
    this.editSubjectPropertyFormStore = new EditSubjectPropertyFormStore(globalStore);
    this.compToolPhotosStore = new CompToolPhotosStore(globalStore);
    this.loading = true;
    this.showCardView = true;
    this.openCarouselPhotos = false;
    this.showCompareScreen = false;
    this.showWarningMessage = false;
    this.currentPage = 1;
    this.editProperty = {};

    this.propertiesMapExtended = false;
    this.isInternal = false;

    makeObservable(this, {
      fetchCompToolProperties: flow,
      fetchFirstThreeProperties: flow,
      fetchCompToolAddressData: flow,
      savePropertyComparableFeedback: flow,
      compResponse: observable,
      firstThreeProperties: observable,
      manuallyAddedComparables: observable,
      addressDataResponse: observable,
      subjectProperty: observable,
      loading: observable,
      setLoading: action,
      selectedProperty: observable,
      setSelectedProperty: action,
      showCardView: observable,
      setShowCardView: action,
      selectedCompareProperties: observable,
      savedPropertyComps: observable,
      addSelectCompareProperties: action,
      addManuallyAddedProperties: action,
      gridApi: observable,
      setGridApi: action,
      openCarouselPhotos: observable,
      setOpenCarouelPhotos: action,
      showCompareScreen: observable,
      setCompareScreen: action,
      fetchPropertiesWithPagination: flow,
      removePropertyFromCompareTool: action,
      showWarningMessage: observable,
      setShowWarningMessage: action,
      currentPage: observable,
      setCurrentPage: action,
      editProperty: observable,
      reset: action,
      propertiesMapExtended: observable,
      setPropertiesMapExtended: action,
      rentalComps: observable,
      setRentalComps: action,
      editSubjectProperty: flow,
      internalUsername: observable,
      setInternalUsername: action,
      loanInformation: observable,
      setLoanInformation: action,
      getCompResponse: action,
      getPropertyById: flow,
      property: observable,
      addressPropertyData: observable,
      savePropertyComps: flow,
      getPropertyComps: flow,
      applyFilters: action,
      sendCompFeedback: flow,
      loadSubjectPropertyCompForm: action,
      compareComments: observable,
      setCompareComments: action,
      onBestComparableChange: action,
      heatmapData: observable,
      fetchHeatmapData: flow,
      clearHeatmapData: action,
      heatmapMinPricePerSqFt: computed,
      heatmapMaxPricePerSqFt: computed,
      setDefaultFilters: action,
      filterOptions: observable,
      addPropertyCompFormStore: action
    });
  }

  *getPropertyById(propertyId: number, callback?: () => void) {
    try {
      if (!propertyId) {
        this.property = null;
        if (callback) callback();
        return;
      }

      const response = yield this.compToolService.getPropertyById(propertyId);

      if (response?.data) {
        this.property = response.data;
      } else {
        this.property = null;
      }

      if (callback) callback();

    } catch (error) {
      this.property = null;
      console.error('Error fetching property by ID:', error);
    }
  }

  getCompResponse() {
    return this.compResponse;
  }

  reset() {
    this.compToolFiltersStore.reset();
    this.editSubjectPropertyFormStore.reset();
    this.compToolPhotosStore.reset();
    this.setShowCardView(true);
    this.editProperty = {};
    this.rentalComps = false;
    this.loanInformation = null;
    this.savedPropertyComps = [];
    this.compResponse = null;
    this.firstThreeProperties = [];
    this.propertyCompFormStores = [];
    this.compareComments = "";
    this.propertiesMapExtended = false;
    this.heatmapData = null;
    this.addressDataResponse = null;
    this.addressPropertyData = null;
    this.manuallyAddedComparables = [];
    this.filterOptions = null;
    this.selectedCompareProperties = [];
  }

  setLoanInformation(loanId: number, propertyId: number) {
    this.loanInformation = {
      loan_id: loanId,
      property_id: propertyId
    };
  }

  setRentalComps(rentalComps) {
    this.rentalComps = rentalComps;
    this.selectedCompareProperties = [];
    this.fetchCompToolProperties(1, true);
  }

  setInternalUsername(username: string) {
    this.internalUsername = username;
  }



  setPropertiesMapExtended(propertiesMapExtended) {
    this.propertiesMapExtended = propertiesMapExtended;
  }

  setCurrentPage(currentPage: number) {
    this.currentPage = currentPage;
  }

  setShowWarningMessage(showWarningMessage: boolean) {
    this.showWarningMessage = showWarningMessage;
  }

  setCompareScreen(value: boolean) {
    this.showCompareScreen = value
  }

  setOpenCarouelPhotos(value: boolean) {
    this.openCarouselPhotos = value
    segmentAnalytics.trackEvent({
      name: SegmentTrackerEventCompTool.COMP_TOOL_OPEN_ALL_PHOTOS,
      userName: this.isInternal ? this.internalUsername : this.userStore.userInformation?.username,
      subjectAddress: this.subjectProperty.data.address,
      propertyAddress: this.selectedProperty.data.address,
      isSubject: this.selectedProperty.is_subject
    });
  }

  setGridApi(value) {
    this.gridApi = value
  }

  removePropertyFromCompareTool(propertyId: string) {
    this.selectedCompareProperties = this.selectedCompareProperties.filter(x => x.property_id !== propertyId);
    this.propertyCompFormStores = this.propertyCompFormStores.filter(x => x.form.fields.propertyId.value !== propertyId);
  }

  clearSelected() {
    const selectedNodes = this.gridApi.getSelectedNodes();

    if (!this.showCardView && selectedNodes.length > 0) {
      selectedNodes.forEach(node => {
        node.setSelected(false);
      });
    }
  }
  addManuallyAddedProperties(property: any) {
    if (!isNil(property.property_id) && property.property_id !== '') {
      if (this.manuallyAddedComparables?.some(p => p.data.address === property.data.address)) {
        if (property.add_manually_status === 'ALREADY_ADDED')
          this.addSelectCompareProperties(property)
        else
          this.globalStore.notificationStore.showErrorNotification({
            message: 'You cannot add the same address twice.',
          });
      } else {
        property.add_manually_status = 'ALREADY_ADDED'
        this.manuallyAddedComparables = [property, ...this.manuallyAddedComparables,]
        this.addSelectCompareProperties(property)
      }
    }
  }

  addSelectCompareProperties(property: ComparableProperty) {
    if (isNil(property.property_id) || property.property_id === '') return;

    if (this.isPropertySelected(property.property_id)) {
      //delete the property if it was selected again
      this.removePropertyFromCompareTool(property.property_id);
    } else {

      this.selectedCompareProperties = [...this.selectedCompareProperties, property];
      this.addPropertyCompFormStore(property);

      segmentAnalytics.trackEvent({
        name: SegmentTrackerEventCompTool.COMP_TOOL_COMP_CHECKED,
        userName: this.isInternal ? this.internalUsername : this.userStore.userInformation?.username,
        subjectAddress: this.subjectProperty.data.address,
        compAddress: property.data.address
      });
    }


  }

  addPropertyCompFormStore(property: ComparableProperty) {
    const store = new PropertyCompFormStore(this.globalStore);
    store.loadForm(this.mapPropertyToStoreObject(property));
    this.propertyCompFormStores.push(store);
  }

  mapPropertyToStoreObject(property: ComparableProperty) {
    let proximity = property?.similarity?.mi_distance;
    if (proximity) {
      proximity = proximity.toFixed(2);
    }
    return {
      propertyId: property.property_id,
      mlsNumber: property.data.mls_id,
      proximity,
      domTrend: property.data.days_on_market,
      propertyType: property?.data?.property_type?.toLowerCase(),
      saleType: capitalizeOnlyFirstLetter(property.data.sale_type),
      lotSize: property.data.lot_size,
      yearBuilt: property.data.year_built,
      priceRent: this.rentalComps ? property.data.rent : property.data.price,
      saleDate: property.data.sale_date ? new Date(property.data.sale_date) : null,
      rooms: property.data.rooms,
      bedrooms: property.data.bedrooms,
      baths: property.data.full_bathrooms,
      halfBaths: property.data.half_bathrooms,
      area: property.data.area,
      pricePerSqFt: property.data.sqft_price,
      taxes: property.data.taxes,
      parkingType: property.data.garage_type,
      hasPool: Boolean(property.data.pool)
    };
  }

  setShowCardView(value: boolean) {
    this.showCardView = value;
    if (!this.showCardView) {
      segmentAnalytics.trackEvent({
        name: SegmentTrackerEventCompTool.COMP_TOOL_LIST_VIEW_CLICKED,
        userName: this.isInternal ? this.internalUsername : this.userStore.userInformation?.username,
        subjectAddress: this.subjectProperty.data.address,
      });
    }

  }

  setSelectedProperty(property: ComparableProperty) {
    this.selectedProperty = property;
  }

  setLoading(value: boolean) {
    this.loading = value;
  }

  *fetchFirstThreeProperties() {
    try {
      const page = 1;
      const numRecords = 3;
      const request = this.getFirstThreePropertiesRequest(page, numRecords);
      const response = yield this.compToolService.getCompToolPropertiesData(request);

      if (!response?.data?.data) {
        return false;
      }

      const parsedResponseData = JSON.parse(response.data.data);
      this.firstThreeProperties = parsedResponseData.properties as ComparableProperty[]
      return true;

    } catch (error) {
      console.error(error)
      return false;
    }

  }

  *fetchCompToolProperties(page, trackEvent = false) {
    try {

      const isFirstRequest = this.compResponse == null;

      const compRequest = this.getCompRequest(page);
      this.setCurrentPage(page);
      const response = yield this.compToolService.getCompToolPropertiesData(compRequest);

      if (!response?.data?.data) {
        this.handleNoDataError();
        this.setLoading(false);
        return false;
      }

      const parsedResponseData = JSON.parse(response.data.data);

      if (!parsedResponseData?.subject_property?.data?.lat ||
        !parsedResponseData?.subject_property?.data?.lng) {
        this.handleNoDataError();
        this.setLoading(false);
        return false;
      }

      let index = ((page - 1) * 8) + 1;
      this.compResponse = {
        ...parsedResponseData,
        properties: parsedResponseData.properties.map((p) => {
          return { ...p, number: p.is_subject ? null : index++ };
        })
      } as CompResponse;

      this.subjectProperty = this.compResponse.subject_property;
      this.loadSubjectPropertyCompForm();
      if (!this.subjectProperty.data.address) {
        this.subjectProperty.data.address = this.searchPropertyFormStore.form.fields.address.value;
      }

      this.filterOptions = this.compResponse.filters_options.fields;
      this.compToolFiltersStore.loadFilterOptions(this.filterOptions);
      this.compToolFiltersStore.loadPresetOptions(this.compResponse.preset_options)
      this.compToolFiltersStore.setSuggestedRanges(this.filterOptions);
      if (isFirstRequest) {
        this.setDefaultFilters();
        this.compToolFiltersStore.setActiveFilters();
      }
      if (trackEvent) {
        this.trackCompToolEvent(this.userStore.userInformation?.username);
      }
      this.getPropertyComps();
      this.setLoading(false);
    } catch (error) {
      console.log(error);
      this.setShowWarningMessage(true)
      this.setLoading(false)
      this.compResponse = null;
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting comp tool data',
      });
      return false;
    }
  }

  *savePropertyComparableFeedback(feedback: PropertyComparableFeedbackRequest[], trackEvent = false) {
    try {
      const response = yield this.compToolService.savePropertyComparableFeedback(feedback);

      if (!response?.data?.data) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'There was an error saving your feedback.',
        });
        return false;
      }
      return true;

    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error saving your feedback.',
      });
      return false;
    }
  }

  *fetchCompToolAddressData(trackEvent = false) {

    try {
      const addressDataRequest = this.getAddressDataRequest();
      const response = yield this.compToolService.getCompToolAddressData(addressDataRequest);

      if (!response?.data?.data) {
        this.setLoading(false);
        this.globalStore.notificationStore.showErrorNotification({
          message: 'We could not find data for this address.',
        });
        return false;
      }

      const parsedResponseData = JSON.parse(response.data.data);

      this.addressDataResponse = {
        addressPropertyData: {
          property_id: generateUUID(),
          is_subject: false,
          similarity: {
            score: 0.0,
            rank: 0,
            mi_distance: 0.0
          },
          data: parsedResponseData.property,
          added_manually_comparable: true,
          add_manually_status: 'NOT_ADDED'
        } as ComparableProperty
      };

      this.addressPropertyData = this.addressDataResponse.addressPropertyData
      this.setLoading(false);
      return true;
    } catch (error) {
      this.setShowWarningMessage(true)
      this.setLoading(false)
      this.addressDataResponse = null;
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting address data.',
      });
      return false;
    }

  }

  handleNoDataError() {
    if (this.compToolFiltersStore.hasActiveFilters()) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'No matching comps were found. Please try using less strict filters.',
      });
    } else {
      this.compResponse = null;
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting comp tool data',
      });
      this.setShowWarningMessage(true);
    }
  }

  trackCompToolEvent(username) {
    const subjectAddress = this.subjectProperty.data.address;
    const eventPayload = {
      userName: username,
      subjectAddress,
    };

    if (this.compToolFiltersStore.hasActiveFilters()) {
      segmentAnalytics.trackEvent({
        ...eventPayload,
        name: SegmentTrackerEventCompTool.COMP_TOOL_FILTERS_SEARCH,
        newFilters: this.compToolFiltersStore.getFiltersRequest(),
      });

    } else if (this.rentalComps) {
      segmentAnalytics.trackEvent({
        ...eventPayload,
        name: SegmentTrackerEventCompTool.COMP_TOOL_RENTAL_REPORTS,
      });
    } else {
      segmentAnalytics.trackEvent({
        ...eventPayload,
        name: SegmentTrackerEventCompTool.COMP_TOOL_SEARCH,
      });
    }
  }

  setDefaultFilters() {
    this.compToolFiltersStore.setDefaultFilters(this.filterOptions);
  }

  getFirstThreePropertiesRequest(page, numRecords) {
    const fields = this.searchPropertyFormStore.form.fields;

    const request =
    {
      address: fields.address.value,
      page: page,
      include_active_listings: false,
      edit_subject_features: this.editProperty,
      rental: this.rentalComps,
      per_page: numRecords,
      lat: fields.latitude.value,
      lng: fields.longitude.value,
      state: fields.state.value,
      mi_distance: 1
    }
    return request;
  }

  getCompRequest(page) {
    const fields = this.searchPropertyFormStore.form.fields;
    const maxDistance = this.compToolFiltersStore.getMinMaxValue('GEO__DIST', 'max');
    const minDistance = this.compToolFiltersStore.getMinMaxValue('GEO__DIST', 'min') || 1;
    const lookbackMonths = this.compToolFiltersStore.getFormValue('MONTHS');
    const includeActiveProperties = this.compToolFiltersStore.getFormValue('ACTIVE_LISTINGS') || false;

    const compRequest =
    {
      address: fields.address.value,
      page: page,
      lookback_months: lookbackMonths,
      include_active_listings: (includeActiveProperties && (includeActiveProperties.includes("True")) ? true : false),
      edit_subject_features: this.editProperty,
      rental: this.rentalComps,
      per_page: 8,
      lat: fields.latitude.value,
      lng: fields.longitude.value,
      state: fields.state.value,
      mi_distance: maxDistance.value ? maxDistance.value : minDistance.value + 1,
      ...((this.compToolFiltersStore.hasActiveFilters() || this.compToolFiltersStore.form.fields.IS_ACTIVE.values.value?.length === 1) && { filters: this.compToolFiltersStore.getFiltersRequest() }),
      ...(this.compToolFiltersStore.getPresetFilter()),
      ...(this.compToolFiltersStore.getPolygonsFilter())
    }
    return compRequest;
  }
  getAddressDataRequest() {
    const fields = this.searchManuallyAddedComparableFormStore.form.fields;
    const addressDataRequest =
    {
      address: fields.address.value,
    }
    return addressDataRequest;
  }


  *fetchPropertiesWithPagination(page) {
    try {
      segmentAnalytics.trackEvent({
        name: SegmentTrackerEventCompTool.COMP_TOOL_PAGE_CLICKED,
        userName: this.userStore.userInformation?.username,
        subjectAddress: this.subjectProperty.data.address,
        currentPage: this.currentPage,
        newPage: page
      });
      yield this.fetchCompToolProperties(page)
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting comp tool data',
      });
      return false;
    }
  }

  *editSubjectProperty() {
    try {
      this.editProperty = this.editSubjectPropertyFormStore.getEditSubjectProperty();
      const {
        rooms,
        bedrooms,
        full_bathrooms,
        half_bathrooms,
        area,
        condition_tag,
        lot_size,
        taxes,
        year_built
      } = this.subjectProperty.data;

      segmentAnalytics.trackEvent({
        name: SegmentTrackerEventCompTool.COMP_TOOL_EDIT_SUBJECT_PROPERTY,
        userName: this.userStore.userInformation?.username,
        subjectAddress: this.subjectProperty.data.address,
        originalData: {
          rooms,
          bedrooms,
          full_bathrooms,
          half_bathrooms,
          area,
          condition_tag,
          lot_size,
          taxes,
          year_built
        },
        editData: this.editProperty
      });

      if (this.subjectProperty.data.property_type_group !== this.editProperty['PROPERTY_TYPE_GROUP']) {
        this.compToolFiltersStore.form.fields.TYPE.values.value = '';
        this.compToolFiltersStore.clearFilter('TYPE');
        this.compToolFiltersStore.setActiveFilters();
      }

      yield this.fetchCompToolProperties(this.currentPage);

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Subject Property succesfully saved',
      });

    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting comp tool data',
      });
      return false;
    }
  }

  *getPropertyComps() {
    try {
      this.savedPropertyComps = [];
      if (!this.property || !this.property.propertyId) {
        return;
      }

      const response = yield this.compToolService.getPropertyCompBySubjectPropertyId(this.property.propertyId);
      this.savedPropertyComps = response.data.data;
    } catch (err) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving comparable properties.',
      });
      console.log(err);
    }
  }

  *savePropertyComps() {
    try {

      if (!this.property || !this.property.propertyId) {
        return;
      }
      const savedPropertyComps = [...this.selectedCompareProperties];
      const subjectPropertyComp = this.mapComparablePropertyToPropertyCompRequest(this.property.propertyId, this.subjectProperty, 0);

      const payload = savedPropertyComps.map((p, index) => this.mapComparablePropertyToPropertyCompRequest(this.property.propertyId, p, index + 1));

      if (!this.isWeightAdjustmentValid(payload)) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Weight adjustment sum should be 100%',
        });
        this.propertyCompFormStores.slice(1)
          .forEach(p => p.form.fields.weightAdjustment.error = "Weight adjustment sum should be 100%");
        return;
      }

      this.propertyCompFormStores.slice(1)
        .forEach(p => p.form.fields.weightAdjustment.error = null);

      payload.push(subjectPropertyComp);
      yield this.compToolService.savePropertyComps(payload);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Property Comps succesfully saved',
      });

      this.savedPropertyComps = payload;


    } catch (err) {
      this.savedPropertyComps = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving comparable properties.',
      });
      console.log(err);
    }

  }

  isWeightAdjustmentValid(payload) {
    const itemsWithWeight = payload.filter(
      item => typeof item.weightAdjustment === 'number'
    );

    if (itemsWithWeight.length === 0) {
      return true;
    }

    const totalWeight = itemsWithWeight.reduce(
      (sum, item) => sum + item.weightAdjustment,
      0
    );
    return totalWeight === 100;
  }

  mapComparablePropertyToPropertyCompRequest(subjectPropertyId: string, comparableProperty: ComparableProperty, sequence: number) {

    const parsedAddress = this.parseAddress(comparableProperty.data.address);
    let formData: any = {};
    if (this.propertyCompFormStores.length > 0) {
      formData = this.propertyCompFormStores.find(store => store.form.fields.propertyId.value === comparableProperty.property_id).getFlattenedValues();
    }

    const propertyComp = {
      subjectPropertyId: subjectPropertyId,
      comparePropertyId: comparableProperty.property_id,
      sequence,
      address: parsedAddress.address,
      city: parsedAddress.city,
      state: parsedAddress.state,
      zipCode: parsedAddress.zip,
      latitude: comparableProperty.data.lat,
      longitude: comparableProperty.data.lng,
      ...formData,
      salePrice: !this.rentalComps ? formData.priceRent : null,
      originalListDate: formatDate(formData.originalListDate, DateFormat.MMDDYYYY),
      saleDate: formatDate(formData.saleDate, DateFormat.MMDDYYYY),
      beds: formData.rooms,
      analystComments: sequence === 0 ? this.compareComments : "",
      propertyImage: getRecommendedPropertyImage(comparableProperty)?.url
    };

    return propertyComp;
  }

  parseAddress(fullAddress: string) {
    if (!fullAddress || typeof fullAddress !== 'string') {
      return { address: null, city: null, state: null, zip: null };
    }

    // Extract ZIP (5 digits, optionally +4) from the end of the string, and the 2-letter
    // state code immediately before it. This mirrors getAddressPart()'s regex approach
    // (utils.ts), because real addresses come as "Street, City, ST ZIP" - state and zip
    // combined in the same trailing segment, not as their own separate comma parts - which
    // a naive comma-count split (the previous implementation here) never matches.
    const zipMatch = fullAddress.match(/\b(\d{5})(?:-\d{4})?\s*$/);
    const zip = zipMatch ? zipMatch[1] : null;

    const stateMatch = fullAddress.match(/\b([A-Za-z]{2})\b,?\s*\d{5}(?:-\d{4})?\s*$/);
    const state = stateMatch ? stateMatch[1].toUpperCase() : null;

    // Whatever precedes the "STATE ZIP" (or bare ZIP) suffix is [street address..., city].
    const cutoffIndex = stateMatch ? stateMatch.index : (zipMatch ? zipMatch.index : fullAddress.length);
    const remainder = fullAddress.slice(0, cutoffIndex).replace(/,\s*$/, '').trim();
    const parts = remainder.split(',').map(p => p.trim()).filter(Boolean);

    const city = parts.length > 1 ? parts[parts.length - 1] : null;
    const address = parts.length > 1 ? parts.slice(0, -1).join(', ') : (parts[0] || null);

    return {
      address: address || null,
      city: city || null,
      state: state || null,
      zip: zip || null
    };
  }

  applyFilters() {
    this.compToolFiltersStore.setActiveFilters();
    this.fetchCompToolProperties(1, true)
  }

  *sendCompFeedback(property) {
    try {
      if (this.compToolFiltersStore.hasActiveFilters()) {
        const compRequest = this.getCompRequest(this.currentPage)
        const response = yield this.compToolFeedbackStore.sendCompToolFeedback(compRequest, this.subjectProperty, property)

      } else {
        const compRequest = {
          address: this.searchPropertyFormStore.form.fields.address.value,
          page: this.currentPage,
        };
        const response = yield this.compToolFeedbackStore.sendCompToolFeedback(compRequest, this.subjectProperty, property)
      }
    }
    catch (error) {
      console.log(error);
      return false;
    }
  }

  loadSubjectPropertyCompForm() {
    this.removePropertyFromCompareTool(this.subjectProperty.property_id);//we do this to not duplicate the subject property form
    this.addPropertyCompFormStore(this.subjectProperty);
  }

  setCompareComments(value: string) {
    this.compareComments = value;
  }

  onBestComparableChange(propertyId: string, value: any) {
    const booleanValue = value === 'true';

    const changedStore = this.propertyCompFormStores.find(store => store.form.fields.propertyId.value === propertyId);
    const othersStores = this.propertyCompFormStores.filter(store => store.form.fields.propertyId.value !== propertyId);

    changedStore.onFieldChange("bestComparable", booleanValue);
    othersStores.forEach(store => store.onFieldChange("bestComparable", !booleanValue));
  }

  *fetchHeatmapData() {
    try {

      const state = getAddressPart(this.subjectProperty.data.address, AddressPart.STATE);
      const heatmapRequest = {
        lat: this.subjectProperty.data.lat,
        lng: this.subjectProperty.data.lng,
        state: state
      };
      const response = yield this.compToolService.getHeatmap(heatmapRequest);

      if (!response?.data?.data) {
        this.globalStore.notificationStore.showErrorNotification({
          message: "We couldn’t find heatmap data for this property.",
        });
        return;
      }

      const parsedData = JSON.parse(response.data.data);
      if (!parsedData?.polygons || parsedData?.polygons?.length === 0) {
        this.globalStore.notificationStore.showErrorNotification({
          message: "We couldn’t find heatmap data for this property.",
        });
        return;
      }

      this.heatmapData = parsedData;

    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error getting the heatmap data for this property.',
      });
      return false;
    }
  }

  clearHeatmapData() {
    this.heatmapData = null;
  }

  get heatmapMinPricePerSqFt() {
    if (!this.heatmapData?.polygons || this.heatmapData?.polygons.length === 0) {
      return 0;
    }

    const polygons = this.heatmapData.polygons;

    // Filter out null, undefined, and zero
    const validValues = polygons
      .map(p => p.value)
      .filter(v => v != null && v !== 0);

    if (validValues.length === 0) {
      return 0;
    }

    const min = Math.min(...validValues);
    return Math.round(min);
  }

  get heatmapMaxPricePerSqFt() {
    if (!this.heatmapData?.polygons || this.heatmapData.polygons.length === 0) {
      return 0;
    }

    const polygons = this.heatmapData.polygons;

    // Filter out null, undefined, and zero
    const validValues = polygons
      .map(p => p.value)
      .filter(v => v != null && v !== 0);

    if (validValues.length === 0) {
      return 0;
    }

    const max = Math.max(...validValues);
    return Math.round(max);
  }

  isPropertySelected(propertyId: string) {
    return this.selectedCompareProperties?.some(p => p.property_id === propertyId);
  }
}
